import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { Button } from './ui/button';
import { Users, Copy, Gift, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const ReferralCard = () => {
  const [referralInfo, setReferralInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const response = await api.get('/referral/info');
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Could not fetch referral info');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (referralInfo?.referral_link) {
      const success = await copyToClipboard(referralInfo.referral_link);
      if (success) {
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 3000);
      } else {
        toast.error('Could not copy. Please copy manually from the link above.');
      }
    }
  };

  const shareViaWhatsApp = () => {
    if (referralInfo?.referral_link) {
      const message = encodeURIComponent(
        `Join Career Step and create your professional CV! Use my referral link and we both get rewards: ${referralInfo.referral_link}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-slate-200 rounded w-full"></div>
      </div>
    );
  }

  if (!referralInfo?.referral_code) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
          <Gift className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Refer & Earn</h3>
          <p className="text-emerald-100 text-sm">Get 15 days FREE for each friend!</p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4">
        <p className="text-xs text-emerald-100 mb-2">Your referral link (click to select):</p>
        <div className="flex items-center gap-2">
          <input 
            type="text"
            readOnly
            value={referralInfo.referral_link}
            onClick={(e) => e.target.select()}
            className="flex-1 text-sm bg-white/20 px-3 py-2 rounded-lg text-white border-0 outline-none cursor-pointer"
          />
          <Button
            onClick={handleCopy}
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={handleCopy}
          className="flex-1 bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Link
        </Button>
        <Button
          onClick={shareViaWhatsApp}
          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold"
        >
          <Share2 className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
      </div>

      <div className="mt-4 pt-4 border-t border-white/20">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-200" />
            <span className="text-emerald-100">Successful referrals:</span>
          </div>
          <span className="font-bold text-lg">{referralInfo.successful_referrals || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default ReferralCard;
